import { useEffect } from 'react'
import type { RefObject } from 'react'

import { usePostComposer } from '~publish/hooks/usePostComposer'

type UseComposerFocusManagementProps = {
  expanded: boolean
  textareaRef: RefObject<HTMLTextAreaElement>
  savedRepliesRef: RefObject<HTMLDivElement>
  emojiPickerRef: RefObject<HTMLDivElement>
}

/**
 * Manages focus restoration to the textarea when the composer closes.
 *
 * This hook monitors the composer's open state and automatically refocuses
 * the textarea when the composer closes, ensuring a smooth user experience
 * when navigating between a reply form and the composer.
 *
 * @param expanded - Whether the reply form is currently expanded
 * @param textareaRef - Reference to the textarea element
 * @param savedRepliesRef - Reference to the saved replies popover element
 * @param emojiPickerRef - Reference to the emoji picker element
 *
 * @example
 * ```tsx
 * const textareaRef = useRef<HTMLTextAreaElement>(null)
 * const savedRepliesRef = useRef<HTMLDivElement>(null)
 * const emojiPickerRef = useRef<HTMLDivElement>(null)
 *
 * useComposerFocusManagement({
 *   expanded,
 *   textareaRef,
 *   savedRepliesRef,
 *   emojiPickerRef,
 * })
 * ```
 */
export const useComposerFocusManagement = ({
  expanded,
  textareaRef,
  savedRepliesRef,
  emojiPickerRef,
}: UseComposerFocusManagementProps): void => {
  const { isOpen: isComposerOpen } = usePostComposer()

  useEffect(() => {
    // Only refocus if reply form is expanded and composer just closed
    if (expanded && !isComposerOpen) {
      // Small delay to ensure composer is fully unmounted
      const timeoutId = setTimeout(() => {
        const textarea = textareaRef.current
        const activeElement = document.activeElement

        // Only refocus if:
        // 1. Textarea exists
        // 2. Focus isn't already on textarea
        // 3. Focus isn't on related elements (saved replies, emoji picker)
        if (
          textarea &&
          activeElement !== textarea &&
          !savedRepliesRef.current?.contains(activeElement) &&
          !emojiPickerRef.current?.contains(activeElement)
        ) {
          textarea.focus()
          const length = textarea.value.length
          textarea.setSelectionRange(length, length)
        }
      }, 100)

      return (): void => clearTimeout(timeoutId)
    }
  }, [isComposerOpen, expanded, textareaRef, savedRepliesRef, emojiPickerRef])
}
